/**
 * Marcar a consulta no SISAC (#1011): o que a tela manda às rotas da agenda e
 * como lê o que elas devolvem. Sem React, para o teste não precisar de tela.
 *
 * ── A tela manda o id da finalidade e os parâmetros pelo nome ────────────
 *
 * O `/v1/me` entrega o mapa da organização (`preferencias.clinica`): finalidade
 * → id da operação. A tela manda o id da finalidade que está usando, e o
 * servidor confere contra o mapa (ADR-0028). Os parâmetros vão com os nomes
 * fixos de cada finalidade; o CRM os põe na posição que a operação declara.
 *
 * ── A hora da vaga é a do relógio da clínica ─────────────────────────────
 *
 * `Agenda.DataHora` é `datetime`, sem fuso. O driver do serviço a devolve como
 * se fosse UTC: a vaga das 08:30 chega como `…T08:30:00.000Z`. Converter para o
 * fuso de quem está na tela a mostraria às 05:30. A tela lê os dígitos do texto
 * e os trata como hora da clínica.
 */

import type { VagaLivre } from './consultas-na-tela';

/** As finalidades sem as quais não se agenda, pela chave em `preferencias.clinica` do `/v1/me`. */
export const CHAVES_DA_AGENDA = ['vagas', 'agendar'] as const;

/** As listas do formulário: sem a operação ligada, o campo vira texto livre. */
export const LISTAS_DA_AGENDA = ['convenios', 'planos', 'origens', 'locais'] as const;
export type ListaDaAgenda = (typeof LISTAS_DA_AGENDA)[number];

export type OperacoesDaAgenda = Record<(typeof CHAVES_DA_AGENDA)[number], string> &
  Partial<Record<ListaDaAgenda, string>>;

/** Quantos dias à frente a tabela de vagas olha. */
export const DIAS_DE_VAGAS = 14;

/**
 * As operações da agenda, ou `null` quando a organização não ligou as vagas ou
 * o agendar. As listas do formulário vêm junto quando ligadas.
 */
export function operacoesDaAgenda(
  clinica: Readonly<Record<string, string>>,
): OperacoesDaAgenda | null {
  const { vagas, agendar } = clinica;
  if (!vagas || !agendar) return null;
  const listas = Object.fromEntries(
    LISTAS_DA_AGENDA.flatMap((lista) => (clinica[lista] ? [[lista, clinica[lista]!]] : [])),
  ) as Partial<Record<ListaDaAgenda, string>>;
  return { vagas, agendar, ...listas };
}

/** `vagas`: o código do médico no SISAC e a janela, de agora a 14 dias (o fim fica de fora). */
export function parametrosDasVagas(codigoDoMedico: string, agora: Date): Record<string, string> {
  const fim = new Date(agora);
  fim.setDate(fim.getDate() + DIAS_DE_VAGAS);
  return { cod_medico: codigoDoMedico, inicio: agora.toISOString(), fim: fim.toISOString() };
}

/** Uma vaga como a leitura de vagas a devolve: o id dela na agenda e a hora da clínica. */
export type VagaDoSisac = { readonly id: string; readonly data_hora: string };

const texto = (valor: unknown): string =>
  typeof valor === 'string' ? valor.trim() : typeof valor === 'number' ? String(valor) : '';

const DATA_HORA = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/** As linhas da leitura de vagas que a tela sabe ler: com `id_agenda` e `data_hora`. */
export function vagasDas(linhas: readonly Readonly<Record<string, unknown>>[]): VagaDoSisac[] {
  return linhas.flatMap((linha) => {
    const id = texto(linha['id_agenda']);
    const dataHora = texto(linha['data_hora']);
    return id !== '' && DATA_HORA.test(dataHora) ? [{ id, data_hora: dataHora }] : [];
  });
}

const doisDigitos = (numero: number) => String(numero).padStart(2, '0');

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

type Relogio = { ano: number; mes: number; dia: number; hora: number; minuto: number };

function curta(r: Relogio, semana: number): string {
  return `${DIAS[semana]!.slice(0, 3)}, ${doisDigitos(r.dia)}/${doisDigitos(r.mes)}, ${doisDigitos(r.hora)}:${doisDigitos(r.minuto)}`;
}

function porExtenso(r: Relogio, semana: number): string {
  return `${DIAS[semana]}, ${r.dia} de ${MESES[r.mes - 1]}, às ${doisDigitos(r.hora)}:${doisDigitos(r.minuto)}`;
}

/** Os dígitos da vaga, sem passar por fuso nenhum. `null` quando o texto não é uma data. */
function relogioDa(vaga: VagaDoSisac): (Relogio & { semana: number }) | null {
  const m = DATA_HORA.exec(vaga.data_hora);
  if (!m) return null;
  const [ano, mes, dia, hora, minuto] = m.slice(1).map(Number);
  // Só para saber o dia da semana; o UTC aqui não converte nada.
  const semana = new Date(Date.UTC(ano!, mes! - 1, dia!)).getUTCDay();
  return { ano: ano!, mes: mes!, dia: dia!, hora: hora!, minuto: minuto!, semana };
}

/**
 * As vagas como o calendário e o resumo as mostram: o dia e a hora da clínica
 * ("2026-10-28", "08:30") e o texto ("qua, 28/10, 08:30").
 */
export function vagasNaTela(vagas: readonly VagaDoSisac[]): VagaLivre[] {
  return vagas.flatMap((vaga) => {
    const r = relogioDa(vaga);
    return r
      ? [
          {
            id: vaga.id,
            dia: `${r.ano}-${doisDigitos(r.mes)}-${doisDigitos(r.dia)}`,
            hora: `${doisDigitos(r.hora)}:${doisDigitos(r.minuto)}`,
            quando: curta(r, r.semana),
            quandoPorExtenso: porExtenso(r, r.semana),
          },
        ]
      : [];
  });
}

/**
 * O instante da vaga, para a consulta do CRM. A hora é a da clínica, e quem
 * marca está no fuso dela: o relógio de quem está na tela dá o fuso.
 */
export function instanteDaVaga(vaga: VagaDoSisac): string | null {
  const r = relogioDa(vaga);
  return r ? new Date(r.ano, r.mes - 1, r.dia, r.hora, r.minuto).toISOString() : null;
}

/** Um instante do CRM por extenso, no relógio de quem lê: "quinta, 6 de novembro, às 10:00". */
export function instantePorExtenso(iso: string): string {
  const d = new Date(iso);
  return porExtenso(
    {
      ano: d.getFullYear(),
      mes: d.getMonth() + 1,
      dia: d.getDate(),
      hora: d.getHours(),
      minuto: d.getMinutes(),
    },
    d.getDay(),
  );
}
